import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DashboardLayout from './layout/DashboardLayout';
import { FaUser, FaEnvelope, FaLock, FaSave, FaGlobe } from 'react-icons/fa';
import CountrySelector from './common/CountrySelector';
import './Profile.css';

function Profile() {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    country: '',
    role: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get('/users/me');
        setFormData(prev => ({
          ...prev,
          firstname: response.data.firstname,
          lastname: response.data.lastname,
          email: response.data.email,
          country: response.data.country,
          role: response.data.role || ''
        }));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch profile data');
      }
    };
    fetchUserData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      const updateData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        country: formData.country
      };
      
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }
      await api.put('/users/me', updateData);
      setSuccess('Profile updated successfully');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  return (
    <DashboardLayout>
      <div className="profile-container">
        <div className="profile-card">

          {/* ── Header ── */}
          <div className="profile-header">
            <div>
              <h2 className="profile-title">
                <FaUser className="profile-icon" /> Profile Settings
              </h2>
              <p className="profile-subtitle">Manage your account information and password</p>
            </div>
          </div>

          {/* ── Alerts ── */}
          {error && (
            <div className="alert alert-danger alert-custom" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>{error}
            </div>
          )}
          {success && (
            <div className="alert alert-success alert-custom" role="alert">
              <i className="bi bi-check-circle me-2"></i>{success}
            </div>
          )}

          {/* ── Form ── */}
          <form onSubmit={handleSubmit}>

            {/* Section: Personal Information */}
            <div className="form-section">
              <h3 className="section-title">
                <FaUser className="me-2" /> Personal Information
              </h3>
              <div className="field-grid">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="firstName"
                    name="firstname"
                    value={formData.firstname || ''}
                    onChange={handleChange}
                    required
                    placeholder="First Name"
                  />
                  <label htmlFor="firstName">First Name</label>
                </div>
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastname"
                    value={formData.lastname || ''}
                    onChange={handleChange}
                    required
                    placeholder="Last Name"
                  />
                  <label htmlFor="lastName">Last Name</label>
                </div>
                <div className="form-floating field-col-2">
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    disabled
                  />
                  <label htmlFor="email">
                    <FaEnvelope className="me-1" /> Email (read-only)
                  </label>
                </div>
                <div className="field-col-2">
                  <CountrySelector
                    value={formData.country}
                    onChange={(val) => setFormData(prev => ({ ...prev, country: val }))}
                    label={<span><FaGlobe className="me-1" /> Country</span>}
                  />
                </div>
              </div>
            </div>

            {/* Section: Change Password */}
            <div className="form-section">
              <h3 className="section-title">
                <FaLock className="me-2" /> Change Password
              </h3>
              <div className="field-grid">
                <div className="form-floating">
                  <input
                    type="password"
                    className="form-control"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword || ''}
                    onChange={handleChange}
                    placeholder="Current Password"
                  />
                  <label htmlFor="currentPassword">Current Password</label>
                </div>
                <div className="form-floating">
                  <input
                    type="password"
                    className="form-control"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword || ''}
                    onChange={handleChange}
                    placeholder="New Password"
                  />
                  <label htmlFor="newPassword">New Password</label>
                </div>
                <div className="form-floating field-col-2">
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword || ''}
                    onChange={handleChange}
                    placeholder="Confirm New Password"
                  />
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-save">
                <FaSave className="me-2" /> Save Changes
              </button>
            </div>

          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Profile;